import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EventService from "../../services/EventService";
import EventShell, { EventError, EventLoading } from "./EventShell";
import { formatEventRange } from "./eventFormat";
import { recallRegistration } from "./EventRegister";

/**
 * Post-event recap at /events/:slug/recap — recording, slides, resource pack,
 * and the certificate of attendance.
 *
 * Attendee-only resources require a registration token, so the page reads the
 * locally remembered one. Without it the visitor still gets the public
 * materials rather than a wall.
 */
const EventRecap = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      const result = await EventService.getEvent(slug);
      if (!active) return;

      if (result.success) {
        setEvent(result.event);
        const token = recallRegistration(slug);
        if (token) {
          const reg = await EventService.getRegistration(token);
          if (active && reg.success) setRegistration(reg.registration);
        }
      } else {
        setError(result.message || "We couldn't find that event.");
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <EventShell narrow>
        <EventLoading label="Loading…" />
      </EventShell>
    );
  }

  if (!event) {
    return (
      <EventShell narrow>
        <EventError message={error || "Event not found."} />
        <Link className="ev-btn ev-btn-secondary" to="/events">
          Browse events
        </Link>
      </EventShell>
    );
  }

  const resources = event.resources || [];
  const attended = registration?.status === "ATTENDED";

  return (
    <EventShell
      narrow
      crumbs={[{ label: "Events", to: "/events" }, { label: event.title }]}
    >
      <h1 className="ev-h1">{event.title}</h1>
      <p className="ev-lead">
        {formatEventRange(event.startsAt, event.endsAt)}
        <br />
        <span className="ev-small">Thanks to everyone who joined us.</span>
      </p>

      {resources.length > 0 ? (
        <div className="ev-panel">
          <h2 className="ev-h2" style={{ marginTop: 0, border: "none" }}>
            Materials
          </h2>
          <ul>
            {resources.map((resource) => (
              <li key={resource.id} style={{ marginBottom: 8 }}>
                <a
                  className="ev-header-link"
                  href={resource.url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {resource.title}
                </a>
                <span className="ev-hint">
                  {" "}
                  · {resource.type.toLowerCase().replace(/_/g, " ")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="ev-alert ev-alert-info">
          Materials from this session aren't posted yet. Check back shortly —
          registrants get an email when they're ready.
        </div>
      )}

      {attended && (
        <div className="ev-panel ev-panel-accent">
          <h2 className="ev-h2" style={{ marginTop: 0, border: "none" }}>
            Certificate of attendance
          </h2>
          <p className="ev-muted">
            Issued to{" "}
            <strong>
              {registration.firstName} {registration.lastName}
            </strong>{" "}
            for attending this session.
          </p>
          <button
            type="button"
            className="ev-btn ev-btn-secondary"
            onClick={() => window.print()}
          >
            Print certificate
          </button>
        </div>
      )}

      <div className="ev-panel">
        <h2 className="ev-h2" style={{ marginTop: 0, border: "none" }}>
          Keep protecting your household
        </h2>
        <p className="ev-muted">
          Check suspicious texts, emails, and links any time — free, no account
          needed.
        </p>
        <div className="ev-btn-row" style={{ marginBottom: 0 }}>
          <a
            className="ev-btn ev-btn-primary"
            href="/?version=standard#tryitout"
          >
            Check something suspicious
          </a>
          <Link className="ev-btn ev-btn-quiet" to="/events">
            See upcoming sessions
          </Link>
        </div>
      </div>
    </EventShell>
  );
};

export default EventRecap;
